'use client'

import { useEffect, useRef, useState } from 'react'

const MOBILE_SKYLINE_SRC =
  '/_next/image?url=%2Fphotos%2Findustrial-skyline.webp&w=750&q=60'

export default function MobileSkylineCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReducedMotion(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || reducedMotion || !window.matchMedia('(max-width: 700px)').matches) return

    let cancelled = false
    const image = new Image()
    image.decoding = 'async'

    const draw = () => {
      if (cancelled || !image.naturalWidth || !image.naturalHeight) return
      const rect = canvas.getBoundingClientRect()
      const width = Math.max(1, Math.round(rect.width))
      const height = Math.max(1, Math.round(rect.height))
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)

      const context = canvas.getContext('2d')
      if (!context) return
      context.setTransform(ratio, 0, 0, ratio, 0, 0)
      const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
      const drawWidth = image.naturalWidth * scale
      const drawHeight = image.naturalHeight * scale
      context.clearRect(0, 0, width, height)
      context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight)
    }

    image.addEventListener('load', draw)
    image.src = MOBILE_SKYLINE_SRC
    const resizeObserver = new ResizeObserver(draw)
    resizeObserver.observe(canvas)

    return () => {
      cancelled = true
      image.removeEventListener('load', draw)
      resizeObserver.disconnect()
    }
  }, [reducedMotion])

  if (reducedMotion) return null
  return <canvas ref={canvasRef} className="fp-skyline-canvas" aria-hidden="true" />
}
